/*
  PDP shoppable videos (snippets/pdp-shoppable-videos.liquid)
  - One tile plays at a time, muted: the left-most tile that is mostly on screen. When it ends,
    the next tile plays; if that tile is off to the side, the carousel scrolls it into view first.
    After the last tile it scrolls back to the first, so playback loops endlessly. A single video
    replays. Every other tile stays paused on its poster, so only one small rendition downloads
    at a time.
  - The speaker icon on a tile makes that tile the playing one with sound on, muting the rest.
  - Tapping a tile opens the video in a pop-up player (native <dialog>) with sound on and full
    controls: play/pause (button or tap on the video), mute, draggable seek bar, time, prev/next
    (buttons, horizontal swipe), fullscreen, and keys (space/k, m, ←/→ seek, f, Esc).
  - Prev/next arrows on the carousel scroll by one column on pointer devices.
*/
if (!customElements.get('pdp-shoppable-videos')) {
  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return '0:00';
    const s = Math.floor(seconds);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  class PdpShoppableVideos extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-video-track]');
      this.items = Array.from(this.querySelectorAll('[data-pdp-video-item]'));
      if (!this.track || !this.items.length) return;

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      this.videos = this.items.map((item) => item.querySelector('video'));
      this.visible = new Set();
      this.active = -1;

      // A tile counts as on screen when 60% of it shows (inside both the carousel and the viewport)
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const index = this.videos.indexOf(entry.target);
          if (entry.intersectionRatio >= 0.6) this.visible.add(index);
          else this.visible.delete(index);
        });
        this.pickActive();
      }, { threshold: [0, 0.6] });
      this.videos.forEach((video) => {
        this.observer.observe(video);
        // Playing tile drops its shade and play symbol
        ['play', 'playing'].forEach((type) => video.addEventListener(type, () => video.parentElement.classList.add('is-playing')));
        ['pause', 'ended', 'emptied'].forEach((type) => video.addEventListener(type, () => video.parentElement.classList.remove('is-playing')));
        video.addEventListener('ended', () => {
          if (this.videos.indexOf(video) === this.active) this.playNext();
        });
      });

      this.prev = this.querySelector('[data-pdp-video-prev]');
      this.next = this.querySelector('[data-pdp-video-next]');
      if (this.prev && this.next) {
        this.updateArrows = this.updateArrows.bind(this);
        this.track.addEventListener('scroll', this.updateArrows, { passive: true });
        this.resizeObserver = new ResizeObserver(this.updateArrows);
        this.resizeObserver.observe(this.track);
      }

      this.setupModal();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.removeEventListener('click', this.onClick);
      if (this.modal?.open) this.closeModal();
    }

    onClick(event) {
      if (event.target.closest('[data-pdp-video-modal]')) return;
      if (event.target.closest('[data-pdp-video-prev]')) return this.scrollByColumn(-1);
      if (event.target.closest('[data-pdp-video-next]')) return this.scrollByColumn(1);

      const item = event.target.closest('[data-pdp-video-item]');
      if (!item) return;

      if (event.target.closest('[data-pdp-video-sound]')) {
        const video = item.querySelector('video');
        this.setSound(video, video.muted);
        return;
      }

      if (this.modal) {
        this.openModal(this.items.indexOf(item));
      } else {
        const video = item.querySelector('video');
        this.setSound(video, video.muted);
      }
    }

    /* Keep the playing tile while it stays on screen, otherwise hand over to the left-most visible one */
    pickActive() {
      if (this.modal?.open) return;
      // Auto-scroll in progress: tiles scroll out before the target scrolls in; don't hand over
      if (this.scrollTarget !== undefined) {
        if (!this.visible.has(this.scrollTarget)) return;
        this.scrollTarget = undefined;
        clearTimeout(this.scrollTimer);
      }
      if (this.visible.has(this.active)) return;
      const visible = [...this.visible].sort((a, b) => a - b);
      this.setActive(visible.length ? visible[0] : -1);
    }

    setActive(index) {
      this.active = index;
      this.videos.forEach((video, i) => {
        if (i === index) return;
        if (!video.paused) video.pause();
        if (!video.muted) this.setSound(video, false);
      });
      if (index > -1) this.videos[index].play().catch(() => {});
    }

    playNext() {
      const next = (this.active + 1) % this.videos.length;
      if (next === this.active) {
        const video = this.videos[this.active];
        video.currentTime = 0;
        video.play().catch(() => {});
        return;
      }

      if (!this.visible.has(next)) this.scrollToTile(next);

      // Sound follows the viewer's choice from one tile to the next
      const withSound = !this.videos[this.active].muted;
      this.setActive(next);
      if (withSound) this.setSound(this.videos[next], true);
    }

    /* Bring a tile into view: it snaps to the left edge, or the carousel stops at its end */
    scrollToTile(index) {
      this.scrollTarget = index;
      clearTimeout(this.scrollTimer);
      // Safety net if the target never reaches 60% visibility (e.g. page scrolled away meanwhile)
      this.scrollTimer = setTimeout(() => {
        this.scrollTarget = undefined;
        this.pickActive();
      }, 1500);

      const left = index === 0 ? 0 : this.items[index].offsetLeft - this.items[0].offsetLeft;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    setSound(video, on) {
      if (on) {
        const index = this.videos.indexOf(video);
        if (index !== this.active) this.setActive(index);
      }
      video.muted = !on;
      if (on && video.paused) video.play().catch(() => {});

      const button = video.parentElement.querySelector('[data-pdp-video-sound]');
      if (button) {
        button.setAttribute('aria-pressed', String(on));
        button.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
      }
    }

    scrollByColumn(direction) {
      const step = this.items[1]
        ? this.items[1].getBoundingClientRect().left - this.items[0].getBoundingClientRect().left
        : this.track.clientWidth;
      this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    updateArrows() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      this.prev.hidden = scrollLeft <= 1;
      this.next.hidden = scrollLeft + clientWidth >= scrollWidth - 1;
    }

    /* ---------------------------------------------------------------------------------------- */
    /* Pop-up player                                                                             */
    /* ---------------------------------------------------------------------------------------- */

    setupModal() {
      const modal = this.querySelector('[data-pdp-video-modal]');
      // No <dialog> support (very old browsers): tiles fall back to the in-place sound toggle
      if (!modal || typeof modal.showModal !== 'function') {
        this.modal = null;
        return;
      }
      this.modal = modal;
      if (modal.dataset.ready) return; // re-connected element: listeners are already attached
      modal.dataset.ready = 'true';

      const $ = (selector) => this.modal.querySelector(selector);
      this.stage = $('[data-pdp-modal-stage]');
      this.player = $('[data-pdp-modal-video]');
      this.seek = $('[data-pdp-modal-seek]');
      this.timeLabel = $('[data-pdp-modal-time]');
      this.counter = $('[data-pdp-modal-counter]');
      this.playButton = $('[data-pdp-modal-play]');
      this.soundButton = $('[data-pdp-modal-sound]');
      this.fullscreenButton = $('[data-pdp-modal-fullscreen]');
      this.modalPrev = $('[data-pdp-modal-prev]');
      this.modalNext = $('[data-pdp-modal-next]');
      this.modalMuted = false;
      this.seeking = false;

      const canFullscreen = document.fullscreenEnabled || document.webkitFullscreenEnabled
        || typeof this.player.webkitEnterFullscreen === 'function';
      this.fullscreenButton.hidden = !canFullscreen;

      this.modal.addEventListener('click', (event) => {
        // Click on the dimmed area around the player
        if (event.target === this.modal) return this.closeModal();
        if (event.target.closest('[data-pdp-modal-close]')) return this.closeModal();
        if (event.target.closest('[data-pdp-modal-play]') || event.target === this.player) return this.togglePlay();
        if (event.target.closest('[data-pdp-modal-sound]')) return this.setModalMuted(!this.player.muted);
        if (event.target.closest('[data-pdp-modal-fullscreen]')) return this.toggleFullscreen();
        if (event.target.closest('[data-pdp-modal-prev]')) return this.showVideo(this.current - 1);
        if (event.target.closest('[data-pdp-modal-next]')) return this.showVideo(this.current + 1);
      });

      // Esc (native cancel) and any other close path
      this.modal.addEventListener('close', () => this.onModalClosed());

      this.modal.addEventListener('keydown', (event) => this.onModalKeydown(event));

      // Seek bar: dragging moves the video; the progress loop leaves it alone meanwhile
      this.seek.addEventListener('pointerdown', () => { this.seeking = true; });
      this.seek.addEventListener('input', () => {
        this.seeking = true;
        if (this.player.duration) this.player.currentTime = (this.seek.value / 1000) * this.player.duration;
        this.renderProgress();
      });
      const endSeek = () => { this.seeking = false; };
      this.seek.addEventListener('change', endSeek);
      this.seek.addEventListener('pointerup', endSeek);
      this.seek.addEventListener('pointercancel', endSeek);

      const syncState = () => {
        this.modal.classList.toggle('is-paused', this.player.paused);
        this.modal.classList.toggle('is-muted', this.player.muted);
        this.playButton.setAttribute('aria-label', this.player.paused ? 'Play' : 'Pause');
        this.soundButton.setAttribute('aria-label', this.player.muted ? 'Unmute' : 'Mute');
      };
      ['play', 'pause', 'volumechange', 'loadstart'].forEach((type) => this.player.addEventListener(type, syncState));
      ['waiting', 'loadstart'].forEach((type) => this.player.addEventListener(type, () => this.modal.classList.add('is-buffering')));
      ['playing', 'canplay', 'pause'].forEach((type) => this.player.addEventListener(type, () => this.modal.classList.remove('is-buffering')));
      ['loadedmetadata', 'durationchange', 'timeupdate', 'seeked'].forEach((type) => this.player.addEventListener(type, () => this.renderProgress()));

      // Horizontal swipe on the video switches videos
      let startX = 0;
      let startY = 0;
      let swipeable = false;
      this.stage.addEventListener('touchstart', (event) => {
        swipeable = !event.target.closest('.pdp-videos-modal__controls') && event.touches.length === 1;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });
      this.stage.addEventListener('touchend', (event) => {
        if (!swipeable || this.items.length < 2) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.showVideo(this.current + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });
    }

    openModal(index) {
      // The pop-up takes over: silence and pause the carousel
      this.setActive(-1);

      this.opener = document.activeElement;
      this.modalMuted = false;
      this.modal.showModal();
      document.documentElement.classList.add('pdp-videos-modal-open');
      this.showVideo(index);
      this.playButton.focus({ preventScroll: true });

      const loop = () => {
        this.renderProgress();
        this.frame = requestAnimationFrame(loop);
      };
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(loop);
    }

    closeModal() {
      if (this.modal.open) this.modal.close();
    }

    onModalClosed() {
      cancelAnimationFrame(this.frame);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.removeAttribute('poster');
      this.player.load();
      document.documentElement.classList.remove('pdp-videos-modal-open');

      // Resume the carousel: one tile, muted
      this.pickActive();
      this.opener?.focus?.({ preventScroll: true });
    }

    showVideo(index) {
      if (index < 0 || index >= this.items.length) return;
      this.current = index;
      const item = this.items[index];

      this.player.src = item.dataset.fullSrc;
      if (item.dataset.fullPoster) this.player.poster = item.dataset.fullPoster;
      else this.player.removeAttribute('poster');
      this.player.setAttribute('aria-label', item.querySelector('video')?.getAttribute('aria-label') || '');
      this.player.muted = this.modalMuted;
      this.seek.value = 0;
      this.renderProgress();

      this.player.play().catch(() => {
        // Browser refused sound without a fresh gesture: fall back to muted playback
        this.setModalMuted(true);
        this.player.play().catch(() => {});
      });

      if (this.counter) this.counter.textContent = `${index + 1} / ${this.items.length}`;
      if (this.modalPrev) this.modalPrev.disabled = index === 0;
      if (this.modalNext) this.modalNext.disabled = index === this.items.length - 1;
    }

    togglePlay() {
      if (this.player.paused) this.player.play().catch(() => {});
      else this.player.pause();
    }

    setModalMuted(muted) {
      this.modalMuted = muted;
      this.player.muted = muted;
    }

    seekBy(seconds) {
      if (!this.player.duration) return;
      this.player.currentTime = Math.min(Math.max(this.player.currentTime + seconds, 0), this.player.duration);
    }

    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      } else if (this.stage.requestFullscreen) {
        this.stage.requestFullscreen().catch(() => {});
      } else if (this.stage.webkitRequestFullscreen) {
        this.stage.webkitRequestFullscreen();
      } else if (this.player.webkitEnterFullscreen) {
        // iPhone: only the video element itself can go fullscreen (native controls)
        this.player.webkitEnterFullscreen();
      }
    }

    renderProgress() {
      const { currentTime, duration } = this.player;
      const ratio = duration ? currentTime / duration : 0;
      if (!this.seeking) this.seek.value = Math.round(ratio * 1000);
      this.seek.style.setProperty('--progress', `${(this.seek.value / 10).toFixed(2)}%`);
      this.seek.setAttribute('aria-valuetext', `${formatTime(currentTime)} of ${formatTime(duration)}`);
      this.timeLabel.textContent = `${formatTime(currentTime)} / ${formatTime(duration)}`;
    }

    onModalKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const onButton = event.target.closest('button');

      switch (event.key) {
        case ' ':
        case 'k':
          if (onButton && event.key === ' ') return; // space activates the focused button
          event.preventDefault();
          this.togglePlay();
          break;
        case 'm':
          this.setModalMuted(!this.player.muted);
          break;
        case 'f':
          this.toggleFullscreen();
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          event.preventDefault(); // also on the seek bar, whose own step (0.1%) is too small to notice
          this.seekBy(event.key === 'ArrowLeft' ? -5 : 5);
          break;
        default:
      }
    }
  }

  customElements.define('pdp-shoppable-videos', PdpShoppableVideos);
}
